/**
 * Surface chrome: the parts that frame a result rather than being the result.
 *
 * All three of these are compliance surfaces as much as UI. The freshness chip is
 * how a user knows a showtime is 40 minutes old; the disclaimer bar is the
 * third-party notice the manifest's policy REQUIRES above the fold; the contrast
 * notice is how a harvested palette that fails WCAG becomes visible instead of
 * shipping as unreadable text.
 */
import { html } from 'htm/preact';
import type { VNode } from 'preact';
import type { Brand, Policy } from '@clawharbor/manifest';
import type { Freshness } from './types.js';
import { auditContrast, describeIssue, type ContrastIssue } from './contrast.js';

export interface FreshnessView {
  state: Freshness['state'];
  ageSec: number | null;
  /** Short chip label. */
  label: string;
  /** Long form for `title`/`aria-label` and for the spoken summary. */
  detail: string;
}

export function humanizeAge(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return 'unknown age';
  if (sec < 45) return 'just now';
  if (sec < 5400) return `${Math.round(sec / 60)} min ago`;
  if (sec < 172_800) return `${Math.round(sec / 3600)} h ago`;
  return `${Math.round(sec / 86_400)} d ago`;
}

/** Derive age from `fetchedAt` when the caller did not compute it. */
export function resolveFreshness(f: Freshness | undefined, now: Date = new Date()): FreshnessView {
  const state = f?.state ?? 'unknown';
  let ageSec = f?.ageSec ?? null;
  if (ageSec == null && f?.fetchedAt) {
    const t = Date.parse(f.fetchedAt);
    if (Number.isFinite(t)) {
      const delta = Math.round((now.getTime() - t) / 1000);
      // A slightly negative age is clock skew between us and the origin; a very
      // negative one means the timestamp is wrong, and claiming "just now" for a
      // timestamp from next week would be a confident lie.
      ageSec = delta >= 0 ? delta : delta > -120 ? 0 : null;
    }
  }
  const age = humanizeAge(ageSec);
  const from = f?.origin ? ` from ${hostOf(f.origin)}` : '';

  switch (state) {
    case 'live':
      return { state, ageSec, label: 'Live', detail: `Fetched${from} just now.` };
    case 'cached':
      return { state, ageSec, label: `Updated ${age}`, detail: `Cached copy${from}, fetched ${age}.` };
    case 'stale':
      return {
        state,
        ageSec,
        label: `Stale · ${age}`,
        detail: `Showing a copy${from} cached ${age} — the live site did not respond.`,
      };
    case 'fixture':
      return { state, ageSec, label: 'Sample data', detail: 'Sample data, not fetched live.' };
    default:
      return { state, ageSec, label: 'Freshness unknown', detail: 'Freshness of this data is unknown.' };
  }
}

/**
 * The chip. `role="status"` rather than a bare span: when the WebMCP page
 * repaints after a tool call, a screen-reader user should hear that the data
 * moved from live to stale without having to go looking for it.
 */
export function renderFreshnessChip(freshness: Freshness | undefined, now?: Date): VNode | null {
  if (!freshness) return null;
  const f = resolveFreshness(freshness, now);
  if (f.state === 'unknown' && !freshness.note) return null;

  return html`
    <span class="ch-chip ch-chip--${f.state}" role="status" title=${f.detail} aria-label=${f.detail}>
      <span class="ch-chip__dot" aria-hidden="true"></span>
      <span>${f.label}</span>
    </span>
  `;
}

export interface DisclaimerOpts {
  /** Rendered as "Source: <origin>" so the user can see whose data this is. */
  origin?: string;
  /** Overrides `policy.takedownUrl`. */
  takedownUrl?: string;
  /** Hide the takedown link (the WebMCP page footer already carries one). */
  hideTakedown?: boolean;
}

/**
 * `checkReferentialIntegrity` already refuses to publish a third-party manifest
 * without a disclaimer, so a missing string here means the tenant is self-owned
 * and the bar correctly disappears.
 */
export function renderDisclaimer(policy: Policy | undefined, opts: DisclaimerOpts = {}): VNode | null {
  const text = policy?.disclaimer;
  if (!text) return null;

  const origin = opts.origin ? hostOf(opts.origin) : null;
  const takedown = opts.hideTakedown ? null : (opts.takedownUrl ?? policy?.takedownUrl);

  return html`
    <aside class="ch-disclaimer" role="note">
      <span>${text}</span>
      ${origin ? html`<span> Source: ${origin}.</span>` : null}
      ${takedown ? html`<span> <a href=${takedown} target="_blank" rel="noopener noreferrer">Report an issue</a>.</span>` : null}
    </aside>
  `;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * Surfaced, not swallowed. A palette scraped off a live site fails AA more often
 * than not, and the operator reviewing a generated manifest is the only person
 * who can fix it — so the failures are rendered where they will be seen.
 */
export function renderContrastNotice(brandOrIssues: Brand | ContrastIssue[] | undefined): VNode | null {
  if (!brandOrIssues) return null;
  const issues = Array.isArray(brandOrIssues) ? brandOrIssues : auditContrast(brandOrIssues);
  const failures = issues.filter((i) => i.status === 'fail');
  if (!failures.length) return null;

  return html`
    <aside class="ch-warn" role="note">
      <strong>${failures.length} colour pair${failures.length === 1 ? '' : 's'} below WCAG AA.</strong>
      <ul>
        ${failures.map((i) => html`<li key=${`${i.fg}|${i.bg}`}>${describeIssue(i)}</li>`)}
      </ul>
    </aside>
  `;
}

export interface HeaderOpts {
  subtitle?: string;
  freshness?: Freshness;
  level?: 2 | 3 | 4;
  now?: Date;
}

export function renderHeader(title: string | undefined, opts: HeaderOpts = {}): VNode | null {
  const chip = renderFreshnessChip(opts.freshness, opts.now);
  if (!title && !chip && !opts.subtitle) return null;
  const Tag = `h${opts.level ?? 2}`;

  return html`
    <header class="ch-header">
      <div>
        ${title ? html`<${Tag} class="ch-title">${title}<//>` : null}
        ${opts.subtitle ? html`<p class="ch-subtitle">${opts.subtitle}</p>` : null}
      </div>
      ${chip}
    </header>
  `;
}
